import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, CheckCircle2, Info, XCircle, X } from "lucide-react";
import { cn } from "@/lib/cn";

const bannerVariants = cva(
  "relative flex items-center gap-3 rounded-md border px-4 py-3 text-sm transition-colors",
  {
    variants: {
      variant: {
        success: "border-success/30 bg-success/10 text-success",
        error: "border-destructive/30 bg-destructive/10 text-destructive",
        warning: "border-warning/30 bg-warning/10 text-warning",
        info: "border-info/30 bg-info/10 text-info",
      },
    },
    defaultVariants: { variant: "info" },
  }
);

const iconMap = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
} as const;

export interface StatusBannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bannerVariants> {
  onDismiss?: () => void;
  title?: string;
}

/**
 * Status banner Railway-style — el verde "Deployment successful" del screenshot.
 * Usar para feedback de operaciones que no requieren intervención.
 */
export function StatusBanner({
  className,
  variant = "info",
  title,
  onDismiss,
  children,
  ...props
}: StatusBannerProps) {
  const Icon = iconMap[variant ?? "info"];
  return (
    <div className={cn(bannerVariants({ variant }), className)} {...props}>
      <Icon className="h-4 w-4 shrink-0" />
      <div className="flex-1 min-w-0">
        {title && <div className="font-medium leading-tight">{title}</div>}
        {children && (
          <div className={cn(title && "mt-1 text-xs opacity-90")}>{children}</div>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
